// AudioPlayer.tsx
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import Colors from '../global/Colors';
import Fonts from '../global/Fonts';
import Button from './Button';
import ButtonMicro from './ButtonMicro';
import Slider from './Slider';
import Tooltip from './Tooltip';

// Static reference to track the currently playing audio element across all instances
const currentlyPlayingAudio = { current: null as HTMLAudioElement | null };

const AudioPlayerContainer = styled.div<{ $colorMode?: 'light' | 'dark', $isPlaying?: boolean, $isSelected?: boolean, $clickable?: boolean }>`
  display: flex;
  flex-direction: row;
  width: 100%;
  background-color: ${props => {
    if (props.$isSelected) {
      return props.$colorMode === 'dark' ? Colors.grey8 : Colors.white;
    }
    if (props.$isPlaying) {
      return props.$colorMode === 'dark' ? Colors.grey8 : Colors.white;
    }
    return props.$colorMode === 'dark' ? Colors.grey9 : Colors.grey1;
  }};
  border: 1px solid ${props => {
    if (props.$isSelected) {
      return props.$colorMode === 'dark' ? Colors.grey7 : Colors.grey3;
    }
    if (props.$isPlaying) {
      return props.$colorMode === 'dark' ? Colors.grey7 : Colors.grey3;
    }
    return props.$colorMode === 'dark' ? Colors.grey8 : Colors.grey2;
  }};
  border-radius: 48px;
  padding: 8px 12px;
  gap: 6px;
  align-items: center;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};

  &:hover {
    border: 1px solid ${props => {
      if (props.$isSelected) {
        return props.$colorMode === 'dark' ? Colors.grey6 : Colors.grey4;
      }
      if (props.$isPlaying) {
        return props.$colorMode === 'dark' ? Colors.grey6 : Colors.grey4;
      }
      return props.$colorMode === 'dark' ? Colors.grey7 : Colors.grey3;
    }};
  }
`;

const InnerColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0px;
`;

const InnerRow = styled(InnerColumn)`
  flex-direction: row;
  gap: 8px;
`;

const TitleText = styled.h4<{ $colorMode?: 'light' | 'dark' }>`
  color: ${props => props.$colorMode === 'dark' ? Colors.grey1 : Colors.grey9};
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 13px;
  line-height: 1.2em;
  text-align: left;
  width: 100%;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 2px 2px 6px 2px;
  margin: 0 auto;
`;

const Controls = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  margin: 0 12px 0 0;
  gap: 8px;
`;

const ProgressContainer = styled.div`
  flex: 1;
  height: 24px;
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: -2px;
`;

const TimeText = styled.div<{ $colorMode?: 'light' | 'dark'; $brightenText: boolean }>`
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 12px;
  color: ${props => {
    if (props.$brightenText) {
      return Colors.grey5;
    }
    return props.$colorMode === 'dark' ? Colors.grey6 : Colors.grey4;
  }};
  white-space: nowrap;
  text-align: left;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const VolumeContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100px;
  margin-left: 8px;
`;

const MiniPlayerContainer = styled(AudioPlayerContainer)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  border-radius: 8px;
  padding: 12px 4px;
  gap: 8px;
`;

const TitleTextMini = styled(TitleText)`
  font-size: 14px;
  text-align: center;
  padding: 2px 6px 6px 6px;
`;

const ControlsMini = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 0px;
`;

const TimeLabelsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  padding: 0 8px;
  margin-top: -4px;
`;

const MiniProgressContainer = styled(ProgressContainer)`
  width: 100%;
  padding: 0 4px;
  margin: 0;
`;

export interface AudioPlayerProps {
  src: string;
  title?: string;
  colorMode?: 'light' | 'dark';
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  autoPlay?: boolean;
  loop?: boolean;
  variant?: 'default' | 'mini';
  onAudioElementPlay?: (audioElement: HTMLAudioElement) => void;
  onBackgroundClick?: () => void;
  isSelected?: boolean;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  src,
  title,
  colorMode = 'light',
  onPlay,
  onPause,
  onEnded,
  autoPlay = false,
  loop = false,
  variant = 'default',
  onAudioElementPlay,
  onBackgroundClick,
  isSelected = false,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const previousVolume = useRef(volume);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoaded(true);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      if (onEnded) onEnded();
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [onEnded]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Add an event listener to update the UI when the audio is paused externally
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePause = () => {
      setIsPlaying(false);
      if (onPause) onPause();
    };

    const handlePlay = () => {
      // Global audio management - pause any other playing audio
      if (currentlyPlayingAudio.current && currentlyPlayingAudio.current !== audio) {
        currentlyPlayingAudio.current.pause();
      }
      
      // Update the global reference to the currently playing audio
      currentlyPlayingAudio.current = audio;
      
      setIsPlaying(true);
      if (onPlay) onPlay();
      if (onAudioElementPlay) onAudioElementPlay(audio);
    };

    audio.addEventListener('pause', handlePause);
    audio.addEventListener('play', handlePlay);

    return () => {
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('play', handlePlay);
    };
  }, [onPause, onPlay, onAudioElementPlay]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      // We don't need to call setIsPlaying here as the pause event listener will handle it
    } else {
      audio.play()
        .then(() => {
          // We don't need to call setIsPlaying here as the play event listener will handle it
        })
        .catch(error => {
          console.error('Error playing audio:', error);
        });
    }
  };

  const handleProgressChange = (value: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = value;
    setCurrentTime(value);
  };

  const handleVolumeChange = (value: number) => {
    setVolume(value);
    previousVolume.current = value;
    setIsMuted(value === 0);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isMuted) {
      setVolume(previousVolume.current || 0.5);
      setIsMuted(false);
    } else {
      previousVolume.current = volume;
      setVolume(0);
      setIsMuted(true);
    }
  };

  // Add this new function to handle background clicks
  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only trigger onBackgroundClick if the click was directly on the container
    // and not on any of its interactive children
    if (e.target === e.currentTarget && onBackgroundClick) {
      onBackgroundClick();
    }
  };

  if (variant === 'mini') {
    return (
      <MiniPlayerContainer 
        $colorMode={colorMode} 
        $isPlaying={isPlaying}
        $isSelected={isSelected}
        onClick={handleContainerClick}
        $clickable={!!onBackgroundClick}
      >
        <audio
          ref={audioRef}
          src={src}
          autoPlay={autoPlay}
          loop={loop}
          preload="metadata"
        />
        {title && <TitleTextMini $colorMode={colorMode}>{title}</TitleTextMini>}
        <Button
          size="small"
          icon={isPlaying ? 'pause' : 'play'}
          onClick={togglePlayPause}
          color={colorMode === 'dark' ? 'white' : 'black'}
          disabled={!isLoaded}
        />
        <ControlsMini>
          <MiniProgressContainer>
            <Slider
              value={currentTime}
              min={0}
              max={duration || 100}
              onChange={handleProgressChange}
              colorMode={colorMode}
              disabled={!isLoaded}
              showLabel={false}
              hideKnobUntilHover={true}
              miniKnob={true}
            />
          </MiniProgressContainer>
          <TimeLabelsContainer>
            <TimeText $colorMode={colorMode} $brightenText={isPlaying || isSelected}>{formatTime(currentTime)}</TimeText>
            <TimeText $colorMode={colorMode} $brightenText={isPlaying || isSelected}>{formatTime(duration || 0)}</TimeText>
          </TimeLabelsContainer>
        </ControlsMini>
      </MiniPlayerContainer>
    );
  } else {
    return (
      <AudioPlayerContainer 
        $colorMode={colorMode} 
        $isPlaying={isPlaying}
        $isSelected={isSelected}
        onClick={handleContainerClick}
        $clickable={!!onBackgroundClick}
      >
        <audio
          ref={audioRef}
          src={src}
          autoPlay={autoPlay}
          loop={loop}
          preload="metadata"
        />
        <Button
          size="tiny"
          icon={isPlaying ? 'pause' : 'play'}
          onClick={togglePlayPause}
          color={colorMode === 'dark' ? 'white' : 'black'}
          disabled={!isLoaded}
        />
        <InnerColumn>
          {title && 
            <InnerRow style={{ padding: '2px 18px 0 4px', marginBottom: '-4px' }}>
              <TitleText $colorMode={colorMode}>{title}</TitleText>
              <TimeText $colorMode={colorMode} $brightenText={isPlaying || isSelected}>
                {formatTime(currentTime)} / {formatTime(duration || 0)}
              </TimeText>
            </InnerRow>
          }
          <Controls>
            {!title && (
              <TimeText $colorMode={colorMode}  $brightenText={isPlaying || isSelected} style={{ width: '22px' }}>
                {formatTime(currentTime)}
              </TimeText>
            )}
            <ProgressContainer>
              <Slider
                value={currentTime}
                min={0}
                max={duration || 100}
                onChange={handleProgressChange}
                colorMode={colorMode}
                disabled={!isLoaded}
                showLabel={false}
                hideKnobUntilHover={true}
                miniKnob={true}
              />
            </ProgressContainer>
            {!title && (
              <TimeText $colorMode={colorMode} $brightenText={isPlaying || isSelected} style={{ width: '22px' }}>
                {formatTime(duration || 0)}
              </TimeText>
            )}
          </Controls>
          
          {/* <VolumeContainer>
            <Button
              size="tiny"
              icon={isMuted ? 'sound' : 'mute'}
              onClick={toggleMute}
              color={colorMode === 'dark' ? 'ghost' : 'ghost white'}
              disabled={!isLoaded}
            />
            <Slider
              value={volume}
              min={0}
              max={1}
              step={0.01}
              onChange={handleVolumeChange}
              colorMode={colorMode}
              disabled={!isLoaded}
              showLabel={false}
              hideKnobUntilHover={true}
            />
          </VolumeContainer> */}
        </InnerColumn>
      </AudioPlayerContainer>
    );
  }
};

export default AudioPlayer;